import { toaster } from '@minetrace/ui'
import { getErrorMessage } from './errorMessages'
import { truncateFileName } from '../lib/inputSanitizers'
import { trackRequest } from '../lib/activity'

/** Límite de caracteres para el nombre transmitido de un archivo (alineado con el backend). */
const MAX_UPLOAD_FILE_NAME = 255

/**
 * Reconstruye un FormData truncando el nombre de cualquier `File` que exceda el
 * límite (preservando la extensión). Es el chokepoint único de todas las subidas
 * multipart del frontend, así que el backend nunca recibe nombres exagerados.
 */
function sanitizeFormDataFileNames(formData: FormData): FormData {
  let needsRewrite = false
  for (const [, value] of formData.entries()) {
    if (value instanceof File && value.name.length > MAX_UPLOAD_FILE_NAME) {
      needsRewrite = true
      break
    }
  }
  if (!needsRewrite) return formData

  const sanitized = new FormData()
  for (const [key, value] of formData.entries()) {
    if (value instanceof File && value.name.length > MAX_UPLOAD_FILE_NAME) {
      const safeName = truncateFileName(value.name, MAX_UPLOAD_FILE_NAME)
      sanitized.append(key, new File([value], safeName, { type: value.type, lastModified: value.lastModified }))
    } else {
      sanitized.append(key, value as string | Blob)
    }
  }
  return sanitized
}

// API Client configuration
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'https://localhost:5051/api'

export class ApiError extends Error {
  errorCode: string | null
  statusCode: number
  errorLogs: string[]

  constructor(message: string, errorCode: string | null, statusCode: number, errorLogs: string[] = []) {
    super(message)
    this.name = 'ApiError'
    this.errorCode = errorCode
    this.statusCode = statusCode
    this.errorLogs = errorLogs
  }
}

export interface PagedResponse<T> {
  items: T[]
  totalCount: number
  pageNumber: number
  pageSize: number
  totalPages: number
  hasNextPage: boolean
  hasPreviousPage: boolean
}

export interface RequestOptions extends RequestInit {
  params?: Record<string, string | number | boolean | undefined>
}

type RefreshSubscriber = (token: string, failure?: Error) => void

/** Tope para renovar el token: pasado esto se suelta la pantalla en vez de congelarla. */
const REFRESH_TIMEOUT_MS = 25_000

type RefreshStateListener = (isRefreshing: boolean) => void
const refreshStateListeners = new Set<RefreshStateListener>()

export function subscribeToRefreshState(listener: RefreshStateListener): () => void {
  refreshStateListeners.add(listener)
  return () => {
    refreshStateListeners.delete(listener)
  }
}

function emitRefreshState(isRefreshing: boolean) {
  refreshStateListeners.forEach((cb) => cb(isRefreshing))
}

class ApiClient {
  private baseUrl: string
  private isRefreshing = false
  private refreshSubscribers: RefreshSubscriber[] = []

  constructor(baseUrl: string) {
    this.baseUrl = baseUrl
  }

  buildUrl(endpoint: string, params?: Record<string, string | number | boolean | undefined>): string {
    const url = new URL(`${this.baseUrl}${endpoint}`)

    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined) {
          url.searchParams.append(key, String(value))
        }
      })
    }

    return url.toString()
  }

  private onRefreshed(token: string) {
    this.refreshSubscribers.forEach((cb) => cb(token))
    this.refreshSubscribers = []
  }

  private rejectRefreshSubscribers(error: Error) {
    const subs = this.refreshSubscribers
    this.refreshSubscribers = []
    subs.forEach((cb) => cb('', error))
  }

  private addRefreshSubscriber(cb: RefreshSubscriber) {
    this.refreshSubscribers.push(cb)
  }

  /**
   * Renueva el token. Devuelve el nuevo token, `null` si el servidor lo RECHAZÓ (sesión
   * vencida → login) o `'unreachable'` si no hubo respuesta a tiempo. Antes no había
   * timeout: con la API colgada (o la máquina sin memoria) el overlay "Refrescando
   * sesión" quedaba encima para siempre y toda petición esperaba detrás → pantalla
   * congelada sin salida.
   */
  private async attemptRefresh(): Promise<string | null | 'unreachable'> {
    const refreshToken = localStorage.getItem('refresh_token')
    if (!refreshToken) return null

    const controller = new AbortController()
    const timer = setTimeout(() => controller.abort(), REFRESH_TIMEOUT_MS)
    try {
      const url = this.buildUrl('/auth/refresh')
      const response = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refreshToken }),
        signal: controller.signal,
      })

      if (!response.ok) return response.status >= 500 ? 'unreachable' : null

      const data = await response.json()
      if (data.accessToken) {
        localStorage.setItem('auth_token', data.accessToken)
        localStorage.setItem('refresh_token', data.refreshToken)
        localStorage.setItem('tenant_id', data.tenantId)
        localStorage.setItem(
          'auth_user',
          JSON.stringify({
            userId: data.userId,
            username: data.username,
            fullName: data.fullName,
            roles: data.roles,
            tenantId: data.tenantId,
            tenantCode: data.tenantCode,
            tenantName: data.tenantName,
            isPlatform: data.isPlatform,
          }),
        )
        return data.accessToken
      }
      return null
    } catch {
      // Timeout o red caída: no es una sesión vencida, no hay que echar al usuario.
      return 'unreachable'
    } finally {
      clearTimeout(timer)
    }
  }

  private clearAuthAndRedirect() {
    localStorage.removeItem('auth_token')
    localStorage.removeItem('refresh_token')
    localStorage.removeItem('auth_user')
    localStorage.removeItem('tenant_id')
    window.location.href = '/login'
  }

  private async handleUnauthorized<T>(
    retryFn: (isRetry: boolean) => Promise<T>,
    endpoint: string,
  ): Promise<T> {
    const refreshToken = localStorage.getItem('refresh_token')
    if (!refreshToken) throw new ApiError('No refresh token', 'NO_REFRESH_TOKEN', 401)

    if (!this.isRefreshing) {
      this.isRefreshing = true
      emitRefreshState(true)
      let result: string | null | 'unreachable'
      try {
        result = await this.attemptRefresh()
        // Un 'unreachable' suele ser la API en arranque en frío (Cloud Run escala a cero):
        // un segundo intento ya la encuentra despierta. Sin esto la pantalla quedaba vacía
        // con la sesión vieja hasta recargar.
        if (result === 'unreachable') result = await this.attemptRefresh()
      } finally {
        this.isRefreshing = false
        emitRefreshState(false)
      }

      if (result && result !== 'unreachable') {
        console.log(`[API] Token refreshed, retrying ${endpoint}`)
        this.onRefreshed(result)
        return retryFn(true)
      }

      // Las peticiones en cola se rechazan: antes quedaban esperando para siempre.
      const error = result === 'unreachable'
        ? new ApiError('No se pudo conectar con el servidor para renovar la sesión. Intenta de nuevo.', 'NETWORK_ERROR', 0)
        : new ApiError('La sesion ha expirado', 'AUTH_REFRESH_TOKEN_EXPIRED', 401)
      this.rejectRefreshSubscribers(error)
      if (result === 'unreachable') {
        console.log(`[API] Token refresh unreachable, keeping session`)
        toaster.error({ description: error.message })
        throw error
      }
      console.log(`[API] Token refresh failed, redirecting to login`)
      this.clearAuthAndRedirect()
      throw error
    } else {
      console.log(`[API] Another refresh in progress, queuing ${endpoint}`)
      return new Promise<T>((resolve, reject) => {
        this.addRefreshSubscriber((_token, failure) => {
          if (failure) reject(failure)
          else resolve(retryFn(true))
        })
      })
    }
  }

  private async request<T>(
    endpoint: string,
    options: RequestOptions = {},
    isRetry = false,
  ): Promise<T> {
    const { params, ...fetchOptions } = options

    const url = this.buildUrl(endpoint, params)
    const method = (fetchOptions.method ?? 'GET').toUpperCase()

    console.log(`[API] ${method} ${endpoint}`, params ?? '')

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    }

    // Add auth token if available
    const token = localStorage.getItem('auth_token')
    if (token) {
      headers['Authorization'] = `Bearer ${token}`
    }

    // Echo the active tenant for logging/debugging — backend trusts the JWT claim
    const tenantId = localStorage.getItem('tenant_id')
    if (tenantId) {
      headers['X-Tenant-Id'] = tenantId
    }

    try {
      const response = await fetch(url, {
        ...fetchOptions,
        headers,
      })

      console.log(`[API] ${method} ${endpoint} -> ${response.status}`)

      // Handle 401 with refresh token logic
      if (response.status === 401 && !isRetry && !endpoint.startsWith('/auth/')) {
        console.log(`[API] 401 detected, attempting token refresh...`)
        return this.handleUnauthorized(() => this.request<T>(endpoint, options, true), endpoint)
      }

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        console.error(`[API] ${method} ${endpoint} ERROR:`, response.status, errorData)
        const rawMessage = errorData.errorLogs?.[0] || `Error ${response.status}: ${response.statusText}`
        const errorCode = errorData.errorCode || null
        const userMessage = getErrorMessage(errorCode, rawMessage)
        toaster.error({ description: userMessage })
        throw new ApiError(rawMessage, errorCode, response.status, errorData.errorLogs || [])
      }

      // Un 200/204 con cuerpo vacío (p. ej. un GET nullable que no encontró
      // registro) hacía que `response.json()` lanzara SyntaxError. Leemos el
      // texto y devolvemos null cuando está vacío, respetando el contrato
      // `T | null` de esos endpoints.
      const text = await response.text()
      const data = text.trim() ? JSON.parse(text) : null
      console.log(`[API] ${method} ${endpoint} DATA:`, data)
      return data as T
    } catch (error) {
      if (error instanceof ApiError) {
        console.error(`[API] ${method} ${endpoint} ApiError:`, error.errorCode, error.message)
        throw error
      }
      console.error(`[API] ${method} ${endpoint} CATCH:`, error)
      throw new ApiError(
        error instanceof Error ? error.message : 'Error de conexion',
        'NETWORK_ERROR',
        0,
      )
    }
  }

  async get<T>(endpoint: string, params?: Record<string, string | number | boolean | undefined>): Promise<T> {
    return trackRequest('GET', () => this.request<T>(endpoint, { method: 'GET', params }))
  }

  async post<T, B = unknown>(endpoint: string, body?: B): Promise<T> {
    return trackRequest('POST', () => this.request<T>(endpoint, {
      method: 'POST',
      body: body ? JSON.stringify(body) : undefined,
    }))
  }

  async put<T, B = unknown>(endpoint: string, body?: B): Promise<T> {
    return trackRequest('PUT', () => this.request<T>(endpoint, {
      method: 'PUT',
      body: body ? JSON.stringify(body) : undefined,
    }))
  }

  async patch<T, B = unknown>(endpoint: string, body?: B): Promise<T> {
    return trackRequest('PATCH', () => this.request<T>(endpoint, {
      method: 'PATCH',
      body: body ? JSON.stringify(body) : undefined,
    }))
  }

  async delete<T>(endpoint: string): Promise<T> {
    return trackRequest('DELETE', () => this.request<T>(endpoint, { method: 'DELETE' }))
  }

  private async requestForm<T>(
    endpoint: string,
    method: 'POST' | 'PUT',
    formData: FormData,
    isRetry = false,
  ): Promise<T> {
    const url = this.buildUrl(endpoint)
    console.log(`[API] ${method} ${endpoint} (multipart)`)

    const safeFormData = sanitizeFormDataFileNames(formData)

    const headers: Record<string, string> = {}
    const token = localStorage.getItem('auth_token')
    if (token) headers['Authorization'] = `Bearer ${token}`
    const tenantId = localStorage.getItem('tenant_id')
    if (tenantId) headers['X-Tenant-Id'] = tenantId

    const response = await fetch(url, { method, headers, body: safeFormData })
    console.log(`[API] ${method} ${endpoint} -> ${response.status}`)

    // Handle 401 with refresh token logic
    if (response.status === 401 && !isRetry && !endpoint.startsWith('/auth/')) {
      console.log(`[API] 401 detected, attempting token refresh...`)
      return this.handleUnauthorized(
        () => this.requestForm<T>(endpoint, method, formData, true),
        endpoint,
      )
    }

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}))
      const rawMessage = errorData.errorLogs?.[0] || `Error ${response.status}`
      const errorCode = errorData.errorCode || null
      toaster.error({ description: getErrorMessage(errorCode, rawMessage) })
      throw new ApiError(rawMessage, errorCode, response.status, errorData.errorLogs || [])
    }

    const data = await response.json()
    return data as T
  }

  /**
   * fetch con la sesión (token + tenant) para lo que no es JSON: descargas, blobs, subidas
   * con FormData propio. Renueva el token ante un 401 y reintenta, igual que request().
   * Antes cada descarga/subida leía el token de localStorage a mano: vencido el token
   * (sesión larga llenando un formulario), la subida del archivo de una solicitud de
   * modificación o la descarga de un documento fallaban con 401 sin renovarlo.
   */
  async fetchAuthorized(url: string, init: RequestInit = {}, isRetry = false): Promise<Response> {
    const headers = new Headers(init.headers)
    const token = localStorage.getItem('auth_token')
    if (token) headers.set('Authorization', `Bearer ${token}`)
    const tenantId = localStorage.getItem('tenant_id')
    if (tenantId && !headers.has('X-Tenant-Id')) headers.set('X-Tenant-Id', tenantId)

    const response = await fetch(url, { ...init, headers })
    if (response.status === 401 && !isRetry) {
      return this.handleUnauthorized(() => this.fetchAuthorized(url, init, true), url)
    }
    return response
  }

  async postForm<T>(endpoint: string, formData: FormData): Promise<T> {
    return trackRequest('POST', () => this.requestForm<T>(endpoint, 'POST', formData))
  }

  async putForm<T>(endpoint: string, formData: FormData): Promise<T> {
    return trackRequest('PUT', () => this.requestForm<T>(endpoint, 'PUT', formData))
  }
}

export const apiClient = new ApiClient(API_BASE_URL)

export default apiClient
